import React, {useCallback} from 'react';

import {createGAEvent} from '@optimaxdev/analytics/desktop/lib/gaEvent/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Modal} from '@optimaxdev/design-system';
import {connect} from 'react-redux';

import {
    CHECKOUT_DELIVERY_STEP,
    CHECKOUT_PAYMENT_STEP,
    CHECKOUT_REVIEW_STEP,
} from 'constants/analytics/events/checkout';
import {PRIVACY_POLICY, TERMS_AND_CONDITIONS} from 'constants/cmsContent';
import {setCmsPopupVisibility} from 'desktop/reducers/cmsPopup';
import {ApplicationStoreType} from 'desktop/store';
import {CmsPopupType} from 'desktop/types/cmsPopupType';

import s from './cmsPopup.scss';

type SideReviewStepNamesType = 'DELIVERY_STEP' | 'PAYMENT_STEP';

export type PropsType = {
    cmsPopup: CmsPopupType;
    setPopupVisibility: typeof setCmsPopupVisibility;
    checkoutEventType?: SideReviewStepNamesType;
};

const TRACKED_POPUP_CHECKOUT: {[key: string]: {message: string}} = {
    [PRIVACY_POLICY]: {message: 'Privacy Policy - closed'},
    [TERMS_AND_CONDITIONS]: {message: 'Terms & Condition - closed'},
};

const CHECKOUT_POPUP_EVENT: {
    [key: string]: {
        name: string;
        method?: string;
    };
} = {
    DELIVERY_STEP: {
        name: 'DELIVERY_STEP',
    },
    PAYMENT_STEP: {
        method: 'Payment',
        name: 'PAYMENT_STEP',
    },
};

/**
 * Popup Wrap
 * @param {PropsType} props - component props
 * @returns {React.ReactElement} - CmsPopup component
 */
export const CmsPopup = ({cmsPopup, setPopupVisibility}: PropsType): React.ReactElement => {
    const getPopupContent = useCallback(() => {
        const {content} = cmsPopup;

        if (content && typeof content === 'object' && 'html' in content)
            return <div dangerouslySetInnerHTML={{__html: content.html}} />;
        return null;
    }, [cmsPopup]);
    const closePopup = useCallback(() => {
        const {params} = cmsPopup;
        setPopupVisibility({content: null, visibility: false});

        if (!params || !TRACKED_POPUP_CHECKOUT[params.cmsBlockName]) return;

        const {checkoutStep, cmsBlockName} = params;

        if (checkoutStep) {
            const {method, name} = CHECKOUT_POPUP_EVENT[checkoutStep];
            pushDataLayer(
                method
                    ? createGAEvent(
                          name === 'DELIVERY_STEP' ? CHECKOUT_DELIVERY_STEP : CHECKOUT_PAYMENT_STEP,
                          {
                              message: TRACKED_POPUP_CHECKOUT[cmsBlockName].message,
                              method,
                              version: 'D',
                          },
                      )
                    : createGAEvent(
                          name === 'DELIVERY_STEP' ? CHECKOUT_DELIVERY_STEP : CHECKOUT_PAYMENT_STEP,
                          {
                              message: TRACKED_POPUP_CHECKOUT[cmsBlockName].message,
                              version: 'D',
                          },
                      ),
            );
        } else {
            sendDesktopAnalyticsEvent(CHECKOUT_REVIEW_STEP, {
                ...TRACKED_POPUP_CHECKOUT[cmsBlockName],
            });
        }
    }, [cmsPopup, setPopupVisibility]);

    return (
        <Modal
            contentClassName={s.popupsCheckout}
            isOpen={Boolean(cmsPopup.visibility)}
            onRequestClose={closePopup}
            overlay="dark"
        >
            <div className={s.popupContent}>{getPopupContent()}</div>
        </Modal>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {{cmsPopup: PropsType}} props
 */
const mapStateToProps = (store: ApplicationStoreType): Pick<PropsType, 'cmsPopup'> => ({
    cmsPopup: store.cmsPopup,
});

const mapDispatchToProps = {
    setPopupVisibility: setCmsPopupVisibility,
};

export const CmsPopupConnected = connect(mapStateToProps, mapDispatchToProps)(CmsPopup);
